'use client';

import { c as _c } from "react/compiler-runtime";
import * as React from 'react';
import { Monitor, Moon, Sun } from 'lucide-react';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuRadioGroup, DropdownMenuRadioItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';

/** The three choices a visitor can make, in menu order. */
const THEMES = [{
  value: 'light',
  label: 'Light',
  Icon: Sun
}, {
  value: 'dark',
  label: 'Dark',
  Icon: Moon
}, {
  value: 'system',
  label: 'System',
  Icon: Monitor
}] as const;
type ThemeValue = (typeof THEMES)[number]['value'];

/**
 * Theme chooser.
 *
 * Offers Light, Dark and System. System is the default and follows the
 * operating system's `prefers-color-scheme`, so a first-time visitor gets
 * whatever their device already asks for; it is offered explicitly as well
 * so someone who has picked light or dark can hand control back rather than
 * being stuck with a choice they made once.
 *
 * The trigger shows the *resolved* appearance (sun or moon) so it always
 * reflects what is on screen, and marks the *selected* mode in the menu so
 * "System" is visible as a distinct state. It renders a hidden placeholder
 * until mount, because the server cannot know the client's theme.
 */
export function ThemeToggle() {
  const $ = _c(20);
  const {
    theme,
    resolvedTheme,
    setTheme
  } = useTheme();
  const [mounted, setMounted] = React.useState(false);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setMounted(true);
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  React.useEffect(t0, t1);
  const isDark = resolvedTheme === "dark";
  const selected = theme as ThemeValue ?? "system";
  let t2;
  if ($[2] !== selected) {
    t2 = THEMES.find(t => t.value === selected)?.label ?? "System";
    $[2] = selected;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const selectedLabel = t2;
  const t3 = mounted ? `Theme: ${selectedLabel}. Change theme` : "Change theme";
  let t4;
  if ($[4] !== isDark || $[5] !== mounted) {
    t4 = mounted ? isDark ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" /> : <Moon className="h-5 w-5 opacity-0" />;
    $[4] = isDark;
    $[5] = mounted;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <span className="sr-only">Change theme</span>;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== t3 || $[9] !== t4) {
    t6 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" size="icon" className="glass rounded-full" aria-label={t3} suppressHydrationWarning={true}>{t4}{t5}</Button></DropdownMenuTrigger>;
    $[8] = t3;
    $[9] = t4;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  if ($[11] !== setTheme) {
    t7 = value => setTheme(value);
    $[11] = setTheme;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = THEMES.map(_temp);
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== selected || $[15] !== t7) {
    t9 = <DropdownMenuContent align="end"><DropdownMenuRadioGroup value={selected} onValueChange={t7}>{t8}</DropdownMenuRadioGroup></DropdownMenuContent>;
    $[14] = selected;
    $[15] = t7;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t6 || $[18] !== t9) {
    t10 = <DropdownMenu>{t6}{t9}</DropdownMenu>;
    $[17] = t6;
    $[18] = t9;
    $[19] = t10;
  } else {
    t10 = $[19];
  }
  return t10;
}
function _temp(t0) {
  const {
    value: value_0,
    label,
    Icon
  } = t0;
  return <DropdownMenuRadioItem key={value_0} value={value_0}><Icon className="mr-2 h-4 w-4" aria-hidden="true" />{label}</DropdownMenuRadioItem>;
}